/**
 * Role-based routing helpers.
 *
 * The backend defines a wide set of role labels — `SuperAdmin`,
 * `TenantAdmin`, `Owner`, `Manager`, `Cashier`, `Staff`, `Accountant`,
 * etc. — and adds new ones as products grow. Hard-coding an allowlist of
 * "panel roles" therefore causes routing regressions whenever a new role
 * name appears.
 *
 * Instead we maintain a small, stable **denylist** of customer-facing
 * roles. Any logged-in user whose role is NOT on that list is treated as
 * a panel user and routed to `/panel/dashboard`. Empty / missing role
 * defaults to customer (safer default for a guest-shopping flow).
 */

const CUSTOMER_ROLES: ReadonlySet<string> = new Set([
  'customer',
  'client',
  'shopper',
  'guest',
  'subscriber',
  'public',
  'enduser',
  'end-user',
  'end_user',
]);

/** True when this role is end-customer-facing (shop / customer dashboard). */
export function isCustomerRole(role: string | null | undefined): boolean {
  if (!role) return true;
  return CUSTOMER_ROLES.has(String(role).trim().toLowerCase());
}

/**
 * True when this role should land on the admin panel after sign-in.
 *
 * Admin / staff / manager / SuperAdmin / TenantAdmin / Owner / etc. all
 * resolve to `true`. Empty role defaults to `false` so anonymous /
 * guest sessions don't accidentally land in the admin UI.
 */
export function isPanelRole(role: string | null | undefined): boolean {
  if (!role) return false;
  return !isCustomerRole(role);
}
